import * as R from 'ramda';
import { mapGetters, mapState } from 'vuex';

import currentViewMixin from '@/mixins/currentView';

import Drawer from '@/components/Dashboard/common/Drawer';
import HelpDesk from '@/components/Common/HelpDesk';
import { WidgetErrorPanelErrorCard } from './WidgetErrorPanelErrorCard';
import { TitleWithTooltip } from './TitleWithTooltip';
import { GuideButtonGroup } from './GuideButtonGroup';

import { Wrapper } from './styles';
import {
    FLEX_VERTICAL_CENTER_DIV,
    HORIZONTAL_SPACER,
} from '@/styles/common.styled.component';

import {
    checkIfWidgetHasDisconnectedDataSource,
    CONNECTION_LOST_WITHIN_7_DAYS,
    DEFAULT_ERROR,
    ERROR_TYPE,
} from '@/utils/dashboard';

export const WidgetErrorPanel = {
    name: 'WidgetErrorPanel',
    mixins: [currentViewMixin],
    props: {
        active: {
            type: Boolean,
            default: false,
        },
        widget: {
            type: Object,
        },
        widgetError: {
            type: Object,
            default() {
                return {};
            },
        },
    },
    data() {
        return {
            isTitleOverflow: false,
            loading: false,
        };
    },
    computed: {
        ...mapState('dashboardHeader', ['widgetRefresh$']),
        ...mapGetters('newDashboard', ['dataSourcesDisconnectedWithin7Days']),

        errorCodeList() {
            return R.compose(
                R.uniqBy((code) => this.$t(`WIDGET.error_panel.error.${code}`)),
                this.setDefaultErrorFirst,
                Object.keys,
                R.propOr({}, 'errors')
            )(this.widgetError);
        },
        widgetErrorErrors() {
            return R.pathOr({}, ['errors'])(this.widgetError);
        },
        adAccountIds() {
            return R.compose(
                R.pluck('adAccountId'),
                R.pathOr({}, ['adAccounts'])
            )(this.widgetError);
        },
        adAccountNames() {
            return R.compose(
                R.pluck('name'),
                R.pathOr({}, ['adAccounts'])
            )(this.widgetError);
        },
        errorCodeToSourceList() {
            return R.pathOr({}, ['errorCodeToSourceList'])(this.widgetError);
        },
        widgetTitle() {
            const widgetTitle = R.pathOr('', [
                'widgetError',
                'displayedWidgetTitle',
            ])(this);
            return widgetTitle;
        },
        callback() {
            return R.prop('callback')(this.widgetError);
        },
    },
    mounted() {
        setTimeout(() => {
            const { scrollWidth, clientWidth } = this.$refs.title || {};

            this.isTitleOverflow = scrollWidth > clientWidth;
        }, 0);
        this.hideChatbot();
    },
    beforeDestroy() {
        this.loadChatbot();
    },
    methods: {
        setDefaultErrorFirst(errorList) {
            return errorList.sort((x, y) =>
                x === DEFAULT_ERROR ? -1 : y === DEFAULT_ERROR ? 1 : 0
            );
        },
    },
    render() {
        const PROPS = {
            attrs: {
                active: this.active,
                headerText: (
                    <FLEX_VERTICAL_CENTER_DIV>
                        {this.$t(`WIDGET.error_panel.title`)}
                        <HORIZONTAL_SPACER amount={16} />
                        <HelpDesk />
                    </FLEX_VERTICAL_CENTER_DIV>
                ),
                onCancel: () => this.$emit('cancel'),
                loadingMask: this.loading,
                closeOutside: true,
                wrapStyle: {
                    transform: 'unset !important',
                },
            },
        };

        return (
            <Drawer {...PROPS}>
                <Wrapper>
                    <GuideButtonGroup />

                    <TitleWithTooltip
                        title={this.widgetTitle}
                        showTooltip={this.isTitleOverflow}
                    />

                    {/* first data disconnection error */}
                    {checkIfWidgetHasDisconnectedDataSource({
                        widget: this.widget,
                        dataSourcesDisconnectedWithin7Days:
                            this.dataSourcesDisconnectedWithin7Days,
                    }) && (
                        <WidgetErrorPanelErrorCard
                            errorCode={CONNECTION_LOST_WITHIN_7_DAYS}
                            sourceList={this.dataSourcesDisconnectedWithin7Days}
                            lastErrorSource={{
                                type: ERROR_TYPE.WARNING,
                                code: CONNECTION_LOST_WITHIN_7_DAYS,
                                canGoToConnection: true,
                            }}
                        />
                    )}
                    {/* errors occurring while fetching data */}
                    {this.errorCodeList.map((errorCode) => {
                        return (
                            <WidgetErrorPanelErrorCard
                                errorCode={errorCode}
                                callback={this.callback}
                                // all data source list this errorCode triggered
                                sourceList={
                                    this.errorCodeToSourceList[errorCode]
                                }
                                // last data source this errorCode triggered
                                lastErrorSource={
                                    this.widgetErrorErrors[errorCode]
                                }
                                widget={this.widget}
                                onSetLoading={(v) => (this.loading = v)}
                            />
                        );
                    })}
                </Wrapper>
            </Drawer>
        );
    },
};
